---
title: "How Biddaan's sales team runs 200 calls a day on NerdCRM"
description: "A founder-eye view of how Nerddevs runs Biddaan's sales and operations team on NerdCRM — what we built, what we learned, and the moment that justified call recording."
seoDescription: "How Biddaan's admissions team runs 200 calls a day on NerdCRM, with every MicroSIP recording auto-linked to the right lead and deal."
publishedAt: "2026-05-10"
slug: "biddaan-admissions-call-recording"
authorPhoto: "/og/sohel-headshot.jpg"
authorSameAs:
  - { label: "LinkedIn", url: "https://www.linkedin.com/in/mdsohelrana/" }
---

# How Biddaan's sales team runs 200 calls a day on NerdCRM

I'm Md Sohel Rana, founder of Nerddevs. We build [Biddaan](https://biddaan.com) — a learning management system for coaching centers and tutoring institutions in Bangladesh. We also build NerdCRM, the CRM you're reading this on. The reason both products exist is not coincidence: we built NerdCRM for ourselves first, to run Biddaan's sales and operations team, and then realized other small phone-heavy sales teams needed the same thing.

This post is the closest thing to an honest case study you'll get out of me. I'll tell you what the team does, what NerdCRM specifically did for them, and why call recording was the feature that earned its keep first.

## What sales and ops actually looks like for Biddaan

Biddaan sells software to a particular kind of customer: a coaching-center owner or tutor who runs their day on the phone. They take admissions over WhatsApp. They chase fee payments by calling parents. They schedule classes through SMS. So when our team picks up the phone to sell Biddaan to them, the conversation is the entire product experience — there's no self-serve onboarding flow that quietly does the work for us.

Across the team we handle around **200 calls a day**:

- Outbound to coaching-center leads who downloaded a brochure or filled the contact form
- Inbound from prospects asking about pricing, batch-management workflows, or how the question bank works
- Onboarding calls walking new clients through their first week on Biddaan
- Renewal and support touchpoints with existing customers

For most of 2024 we ran this on a spreadsheet plus WhatsApp. The spreadsheet captured who-talked-to-whom; WhatsApp captured the actual call audio when reps remembered to forward it. Neither was queryable. Both leaked.

## The specific moment that made us rip out the spreadsheet

Sometime last year, a rep said the prospect they spoke to "wasn't interested." A week later that prospect called back, asked for someone else, and said the original rep had been rude. We had no recording. We had a one-line note that said "not interested." There was no way to figure out what actually happened.

Two weeks later it happened again with a different rep. That's when I started writing the recording-link feature on a Saturday.

## What NerdCRM does for the sales team today

The day-to-day for a Biddaan sales rep on NerdCRM:

1. **Open the Kanban board** to see deals in their callback queue. Each card shows the coaching center's name, the program size they're running, and the last call's outcome.
2. **Click a callback** — the deal page shows every prior call as a recording, in order. They listen to the last one in 90 seconds and remember the conversation before they dial.
3. **Place the call through MicroSIP**. The call is recorded automatically; the recorder agent uploads it to S3; the server attaches it to the deal by the time the call ends.
4. **Drag the card** to the next stage — `Demo scheduled`, `Trial started`, `Onboarded`, or `Lost`. That movement triggers the activity log and the weekly pipeline report.

The spreadsheet is gone. WhatsApp is for personal messages again.

## The numbers that justified the feature

Some are concrete, some aren't:

- Rep time saved per call by skipping "what did we say last time": **~2 minutes/call**
- Calls per rep per month: **~600**
- Rep-hours saved monthly per seat: **~20 hours**
- Disputes resolved by replaying the call: **~2–3 per month**

I'd love to give you more precise numbers, but the real ones are tangled with marketing spend and customer-success metrics that aren't mine to share. The shape is what matters: the recording feature paid for itself in the first month per rep, every month, for as long as the team is on the phone.

## Why we shipped this as NerdCRM and not just as an internal tool

Two reasons:

1. **The shape generalizes.** Recruiters, real-estate brokers, BPO outbound teams, other EdTech sales orgs — anyone whose product is "the conversation" needs this. We knew, because we got two unsolicited referrals to friends-of-friends-of-Biddaan who wanted the same thing.
2. **Internal tools rot.** The fastest way to keep a tool good is to make customers depend on it. Selling NerdCRM externally forced us to put real engineering hours into the things that would have stayed scrappy if it were just for us — the demo, the onboarding, the storage policy, the comparison pages, the documentation.

## What's next

I'm focused on tightening the recording experience itself — better matching of recordings to deals when the phone-number lookup misses, faster playback navigation, and cleaner exports for compliance asks. The same internal-first pattern: Biddaan's team gets it first, we measure whether it actually moved the needle, and only then does it ship to other customers.

If you run a phone-heavy sales team and the spreadsheet-and-WhatsApp setup sounds familiar, the [live demo](/demo) is seeded with a sales-and-ops pipeline you can poke at without signing up. Or come find me on [LinkedIn](https://www.linkedin.com/in/mdsohelrana/).
